import { useEffect } from "react";
import Script from "next/script";
import { Breadcrumb } from "@/types/types";
import { dispatchAnalyticsEvent } from "@/utils";
import { usePrivacyFunctionalAccepted } from "@/hooks/usePrivacyFunctionalAccepted/usePrivacyFunctionalAccepted";
import {
  FitFinderClickEvents,
  FitFinderListenerEvents,
  FitFinderImpressions,
} from "@/components/FitToolAndSizeChart/components/FitFinder/FitFinderUtils";
import { sendEvent } from "@JohnLewisPartnership/experiments-utils";

declare const window: any; // eslint-disable-line @typescript-eslint/no-explicit-any

type FitFinderSize = {
  value: string;
  isAvailable: boolean;
};

interface FitDetail {
  breadcrumb: Breadcrumb[];
  price: string;
  productName: string;
  productId: string;
  brand: string;
  category: string | undefined;
  sku: string;
}

type FitFinderProps = {
  mainThumbnail: string;
  currentColour: string;
  sizes: Array<FitFinderSize>;
  brand: string;
  crumbs: Array<Breadcrumb>;
  price: string;
  skuTitle: string;
  productId: string;
  currentSkuId: string;
  fitFinderUrl: string;
  handleRecordImpression: (state: string, impressionStatus: boolean) => void;
};

export const FitFinderUI = ({
  mainThumbnail,
  currentColour,
  sizes,
  brand,
  price,
  crumbs,
  skuTitle,
  productId,
  currentSkuId,
  fitFinderUrl,
  handleRecordImpression,
}: FitFinderProps) => {
  const hasAcceptedPrivacyTerms = usePrivacyFunctionalAccepted();

  useEffect(() => {
    window.fitAnalyticsData = {
      shopLanguage: "en",
      shopCountry: "GB",
      mainThumbnail: mainThumbnail,
      currentProductId: productId,
      currentColour: currentColour,
      allProductIds: [productId],
      experimentFunnel: "true",
      sizes: sizes,
    };

    const handleFitAnalyticsAvailableEvent = () => {
      sendEvent(FitFinderClickEvents.available);
      handleRecordImpression(FitFinderImpressions.available, true);
    };

    const handlesizeRecommendedEvent = () => {
      sendEvent(FitFinderClickEvents.sizeRecommended);
    };

    const handleimmediateRecommendationEvent = () => {
      sendEvent(FitFinderClickEvents.immediateRecommendation);
    };

    const handleinitialRecommendationEvent = () => {
      sendEvent(FitFinderClickEvents.initialRecommendation);
    };

    const handleOpenAnalitycsEvent = () => {
      const analyticsDetail = {
        breadcrumb: crumbs,
        price: price,
        productName: skuTitle,
        productId: productId,
        brand: brand,
        category: crumbs[crumbs.length - 1]?.value,
        sku: currentSkuId,
      };
      sendEvent(FitFinderClickEvents.opened);
      dispatchFitAnalyticsEvent(analyticsDetail);
      handleRecordImpression(FitFinderImpressions.opened, true);
    };

    document.body.addEventListener(
      FitFinderListenerEvents.available,
      handleFitAnalyticsAvailableEvent,
    );
    document.body.addEventListener(
      FitFinderListenerEvents.opened,
      handleOpenAnalitycsEvent,
    );
    document.body.addEventListener(
      FitFinderListenerEvents.sizeRecommended,
      handlesizeRecommendedEvent,
    );
    document.body.addEventListener(
      FitFinderListenerEvents.immediateRecommendation,
      handleimmediateRecommendationEvent,
    );
    document.body.addEventListener(
      FitFinderListenerEvents.initialRecommendation,
      handleinitialRecommendationEvent,
    );

    return () => {
      document.body.removeEventListener(
        FitFinderListenerEvents.available,
        handleFitAnalyticsAvailableEvent,
      );
      document.body.removeEventListener(
        FitFinderListenerEvents.opened,
        handleOpenAnalitycsEvent,
      );
      document.body.removeEventListener(
        FitFinderListenerEvents.sizeRecommended,
        handlesizeRecommendedEvent,
      );
      document.body.removeEventListener(
        FitFinderListenerEvents.immediateRecommendation,
        handleimmediateRecommendationEvent,
      );
      document.body.removeEventListener(
        FitFinderListenerEvents.initialRecommendation,
        handleinitialRecommendationEvent,
      );
    };
  }, [currentSkuId]);

  return (
    <>
      {hasAcceptedPrivacyTerms && (
        <Script
          data-testid={"fit-finder-script"}
          src={fitFinderUrl}
          type="text/javascript"
        />
      )}
      <div
        id="fitanalytics-size-advisor"
        aria-label="fit analytics"
        data-component="FitFinder"
      ></div>
    </>
  );
};

const dispatchFitAnalyticsEvent = (detail: FitDetail) => {
  dispatchAnalyticsEvent(
    "global.fitFinder",
    {
      location: "pdp",
      pageName: "jl:fit finder:your measurements",
      breadCrumb: [
        {
          value: detail.breadcrumb[0],
          clickable: 1,
        },
        {
          value: detail.breadcrumb[1],
          clickable: 1,
        },
      ],
      product: {
        price: detail.price,
        productName: detail.productName,
        productId: detail.productId,
        brand: detail.brand,
        category: detail.category,
        displayedSku: detail.sku,
      },
    },
    {},
  );
};
